// "What is this faint thing?" When zoomed in on the sky photographs, a tap on something the app's own
// catalogues do not hold asks SIMBAD (CDS Strasbourg), the reference database of objects beyond the
// Solar System. Only the tapped sky position is sent. Free to use; credit "SIMBAD, CDS Strasbourg".
import { fetchWithin } from '../lib/net';

export interface SimbadObject {
  kind: 'simbad';
  id: string; // SIMBAD's main identifier, as SIMBAD writes it (for the link)
  name: string; // tidied for reading
  otype: string;
  ra: number; // degrees, ICRS
  dec: number;
  v?: number;
  plx?: number; // mas
  z?: number;
}

/** Object types worth naming to a stargazer (SIMBAD short codes); radio blobs and parts of galaxies are skipped. */
const TYPES: Record<string, string> = {
  '*': 'Star', '**': 'Double or multiple star', 'EB*': 'Eclipsing binary star', 'SB*': 'Spectroscopic binary star', 'V*': 'Variable star',
  'Pu*': 'Pulsating variable star', 'RR*': 'RR Lyrae variable star', 'Ce*': 'Cepheid variable star', 'Mi*': 'Mira variable star', 'LP*': 'Long-period variable star',
  'PM*': 'Fast-moving nearby star', 'HV*': 'High-velocity star', 'RG*': 'Red giant star', 'AB*': 'Asymptotic giant branch star', 'C*': 'Carbon star',
  'S*': 'S-type star', 'WD*': 'White dwarf', 'Em*': 'Emission-line star', 'Be*': 'Be star', 'BS*': 'Blue straggler star', 'HB*': 'Horizontal branch star',
  'sg*': 'Supergiant star', 's*r': 'Red supergiant star', 's*y': 'Yellow supergiant star', 's*b': 'Blue supergiant star', 'WR*': 'Wolf-Rayet star',
  'Y*O': 'Young star', 'Or*': 'Young variable star', 'TT*': 'T Tauri star', 'Ae*': 'Herbig Ae/Be star', 'No*': 'Nova', 'SN*': 'Supernova',
  'LM*': 'Low-mass star', 'BD*': 'Brown dwarf', 'Pl': 'Planet around another star', 'MS*': 'Main-sequence star', 'Ev*': 'Evolved star', 'OH*': 'OH/IR star', 'CV*': 'Cataclysmic variable star',
  'Cl*': 'Star cluster', 'GlC': 'Globular cluster', 'OpC': 'Open cluster', 'As*': 'Group of stars', 'MGr': 'Moving group of stars',
  'PN': 'Planetary nebula', 'HII': 'Glowing gas cloud (H II region)', 'RNe': 'Reflection nebula', 'SNR': 'Supernova remnant', 'DNe': 'Dark cloud',
  'MoC': 'Molecular cloud', 'Cld': 'Cloud of gas and dust', 'GNe': 'Nebula', 'bub': 'Bubble in space', 'ISM': 'Interstellar matter',
  G: 'Galaxy', GiG: 'Galaxy in a group', GiC: 'Galaxy in a cluster', GiP: 'Galaxy in a pair', IG: 'Interacting galaxies', PaG: 'Pair of galaxies',
  GrG: 'Group of galaxies', ClG: 'Cluster of galaxies', SBG: 'Starburst galaxy', LSB: 'Low-surface-brightness galaxy', bCG: 'Blue compact galaxy',
  EmG: 'Emission-line galaxy', H2G: 'H II galaxy', AGN: 'Galaxy with an active nucleus', SyG: 'Seyfert galaxy', Sy1: 'Seyfert galaxy', Sy2: 'Seyfert galaxy',
  LIN: 'LINER galaxy', rG: 'Radio galaxy', QSO: 'Quasar', Bla: 'Blazar', BLL: 'BL Lacertae object',
};
export const typeName = (otype: string) => TYPES[otype] ?? null;

const GREEK: Record<string, string> = {
  alf: 'α', bet: 'β', gam: 'γ', del: 'δ', eps: 'ε', zet: 'ζ', eta: 'η', tet: 'θ', iot: 'ι', kap: 'κ', lam: 'λ', mu: 'μ',
  nu: 'ν', ksi: 'ξ', omi: 'ο', pi: 'π', rho: 'ρ', sig: 'σ', tau: 'τ', ups: 'υ', phi: 'φ', chi: 'χ', psi: 'ψ', ome: 'ω',
};
/** "* alf And" → "α And", "NAME Pinwheel Galaxy" → "Pinwheel Galaxy", "V* R Leo" → "R Leo", "M  31" → "M 31". */
export function tidyName(id: string): string {
  let n = id.replace(/\s+/g, ' ').trim();
  n = n.replace(/^NAME /, '').replace(/^(V\*|\*\*|\*) /, '');
  n = n.replace(/^([a-z]{2,3})(\d*) /, (m, g: string, k: string) => (GREEK[g] ? `${GREEK[g]}${k ? String(+k).replace(/\d/g, (c) => '⁰¹²³⁴⁵⁶⁷⁸⁹'[+c]) : ''} ` : m));
  return n;
}

/** The best-known object near a sky position (degrees), or null. `radius` in degrees. */
export async function simbadNear(ra: number, dec: number, radius: number, signal?: AbortSignal): Promise<SimbadObject | null> {
  const r = Math.min(Math.max(radius, 1 / 3600), 0.5);
  const q =
    `SELECT TOP 60 main_id, otype, ra, dec, V, plx_value, rvz_redshift, DISTANCE(POINT('ICRS', ra, dec), POINT('ICRS', ${ra.toFixed(6)}, ${dec.toFixed(6)})) AS d ` +
    `FROM basic LEFT JOIN allfluxes ON oid = allfluxes.oidref ` +
    `WHERE CONTAINS(POINT('ICRS', ra, dec), CIRCLE('ICRS', ${ra.toFixed(6)}, ${dec.toFixed(6)}, ${r.toFixed(6)})) = 1 ORDER BY d`;
  // The nearest nameable object, favouring brighter ones a little (a bright star just beside a faint
  // smudge is usually what was meant).
  let best: SimbadObject | null = null, bestScore = Infinity;
  for (const row of await tap(q, signal)) {
    const [, otype, , , v, , , d] = row;
    if (!typeName(otype)) continue;
    const score = d / r + (v != null ? Math.max(0, v - 6) * 0.04 : 0.5);
    if (score < bestScore) {
      bestScore = score;
      best = toObject(row);
    }
  }
  return best;
}

/** One object by its SIMBAD identifier (for shared links), or null. */
export async function simbadById(id: string, signal?: AbortSignal): Promise<SimbadObject | null> {
  if (!id || id.length > 80) return null;
  const q =
    `SELECT main_id, otype, ra, dec, V, plx_value, rvz_redshift, 0 AS d FROM basic LEFT JOIN allfluxes ON oid = allfluxes.oidref ` +
    `WHERE main_id = '${id.replace(/'/g, "''")}'`;
  const row = (await tap(q, signal))[0];
  return row && typeName(row[1]) ? toObject(row) : null;
}

type Row = [string, string, number, number, number | null, number | null, number | null, number];

async function tap(q: string, signal?: AbortSignal): Promise<Row[]> {
  const body = new URLSearchParams({ REQUEST: 'doQuery', LANG: 'ADQL', FORMAT: 'json', QUERY: q });
  // Its own deadline as well as the caller's: a stalled answer fails like any other, about when the sky's
  // "Looking it up…" note goes.
  const res = await fetchWithin('https://simbad.cds.unistra.fr/simbad/sim-tap/sync', (r) => {
    if (!r.ok) throw new Error(`SIMBAD ${r.status}`);
    return r.json() as Promise<{ data?: Row[] }>;
  }, { method: 'POST', body, signal, ms: 12e3 });
  return res.data ?? [];
}

const toObject = ([id, otype, ra, dec, v, plx, z]: Row): SimbadObject => ({
  kind: 'simbad', id, name: tidyName(id), otype, ra, dec, v: v ?? undefined, plx: plx != null && plx > 0.2 ? plx : undefined, z: z ?? undefined,
});

/** Distance in words: from parallax for stars, from redshift for galaxies. */
export function distanceWords(o: SimbadObject): string | null {
  if (o.plx) {
    const ly = 3261.56 / o.plx;
    return ly < 100 ? `about ${Math.round(ly)} light-years` : ly < 10000 ? `about ${(Math.round(ly / 10) * 10).toLocaleString('en-GB')} light-years` : `about ${(Math.round(ly / 1000) * 1000).toLocaleString('en-GB')} light-years`;
  }
  if (o.z != null && o.z > 0.001 && o.z < 0.3 && /G|QSO|Sy|AGN|LIN|Bla|BLL/.test(o.otype)) {
    const mly = (o.z * 299792.458) / 70 * 3.2616; // Hubble's law, fine for nearby galaxies
    return mly < 1000 ? `about ${Math.round(mly)} million light-years` : `about ${(mly / 1000).toFixed(1)} billion light-years`;
  }
  return null;
}
